import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

// ── Radix primitives ─────────────────────────────────────────

export const DialogRoot      = RadixDialog.Root;
export const DialogTrigger   = RadixDialog.Trigger;
export const DialogClose     = RadixDialog.Close;
export const DialogPortal    = RadixDialog.Portal;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Overlay>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Overlay>
>(({ className, ...props }, ref) => (
  <RadixDialog.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-in fade-in',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = RadixDialog.Overlay.displayName;

const sizeMap = {
  sm:   'max-w-[400px]',
  md:   'max-w-[512px]',
  lg:   'max-w-[640px]',
  xl:   'max-w-[768px]',
  full: 'max-w-[95vw]',
} as const;

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof RadixDialog.Content> {
  size?: keyof typeof sizeMap;
  hideClose?: boolean;
  /** ID of the title element for aria-labelledby */
  titleId?: string | undefined;
  /** ID of the description element for aria-describedby */
  descriptionId?: string | undefined;
  /** Override the backdrop styling (e.g. a lighter overlay for side drawers). */
  overlayClassName?: string | undefined;
}

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Content>,
  DialogContentProps
>(({ className, children, size = 'md', hideClose = false, titleId, descriptionId, overlayClassName, ...props }, ref) => (
  <RadixDialog.Portal>
    <DialogOverlay className={overlayClassName} />
    <RadixDialog.Content
      ref={ref}
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2 p-6',
        'bg-surface-overlay border border-border-subtle rounded-2xl shadow-xl',
        'animate-in fade-in slide-in-from-bottom-4 duration-slow',
        'focus:outline-none',
        sizeMap[size],
        className,
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <RadixDialog.Close
          className="absolute right-4 top-4 rounded-md p-1 text-text-tertiary hover:bg-bg-subtle hover:text-text-primary transition-colors focus-visible:outline-2 focus-visible:outline-brand-default focus-visible:outline-offset-2"
          aria-label="Close dialog"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </RadixDialog.Close>
      )}
    </RadixDialog.Content>
  </RadixDialog.Portal>
));
DialogContent.displayName = RadixDialog.Content.displayName;

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-4 pr-6', className)} {...props} />;
}

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Title>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(({ className, ...props }, ref) => (
  <RadixDialog.Title
    ref={ref}
    className={cn('text-lg font-semibold text-text-primary', className)}
    {...props}
  />
));
DialogTitle.displayName = RadixDialog.Title.displayName;

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Description>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(({ className, ...props }, ref) => (
  <RadixDialog.Description
    ref={ref}
    className={cn('text-sm text-text-secondary mt-1', className)}
    {...props}
  />
));
DialogDescription.displayName = RadixDialog.Description.displayName;

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'mt-4 flex items-center justify-end gap-3 border-t border-border-subtle pt-4',
        className,
      )}
      {...props}
    />
  );
}

// ── Backward-compat simple Dialog (matches existing { open, onClose, title, ... } API) ──

interface SimpleDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  size?: keyof typeof sizeMap;
}

export function Dialog({ open, onClose, title, description, children, size = 'md' }: SimpleDialogProps) {
  return (
    <DialogRoot open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size={size}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
      </DialogContent>
    </DialogRoot>
  );
}
